/**
 * MAESTRO-Assistent — agent-native Chat-Oberflaeche (docs/KONZEPT-AGENT-NATIVE.md, Phase 0).
 * Der gesamte Speisekarten-Review passiert IM CHAT: der Agent liefert typisierte KACHELN
 * (menu_preview, spell_suggestions, allergen_confirm, approval_gate), die als interaktive
 * Bausteine im Gespraechsfaden gerendert werden.
 *
 * Diese Seite ist die eigenstaendige Fallback-Ansicht (Route /assistent). Die Chat-Logik lebt
 * seit MAESTRO 2.0 wiederverwendbar im Hook useAgentSession, die Kacheln in ui/agent-cards —
 * dieselbe Basis nutzt die global angedockte Copilot-Spalte (ui/CopilotPanel). Verhalten
 * unveraendert: Request/Response + loadHistory() nach jeder Mutation, kein Streaming.
 */
import { useEffect, useRef, useState } from "react";
import { Button, ErrorNote, Icon, Card } from "../ui/components";
import { CardView } from "../ui/agent-cards";
import { useAgentSession, textOf, stripMarkdown, internalContextLabel } from "../hooks/useAgentSession";

const MAX_FILE_BYTES = 8 * 1024 * 1024;

const SUGGESTIONS: { icon: string; title: string; prompt: string }[] = [
  { icon: "description", title: "Neue Speisekarte einlesen", prompt: "Ich möchte eine Speisekarte einlesen. Ich hänge sie gleich an." },
  { icon: "request_quote", title: "Angebot erstellen", prompt: "Hilf mir, ein Angebot für einen Vorgang zu erstellen." },
  { icon: "inbox", title: "Anfragen ansehen", prompt: "Welche Anfragen warten gerade auf eine Antwort?" },
];

export default function AssistentPage() {
  const { messages, cards, busy, loading, error, setError, send, runAction, approve, reject } = useAgentSession();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, cards, busy]);

  function addFiles(list: FileList | null) {
    if (!list) return;
    const picked = Array.from(list);
    const tooBig = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setError(`Datei zu groß: ${tooBig.name} (max. 8 MB).`);
      return;
    }
    setError(null);
    setFiles((prev) => [...prev, ...picked].slice(0, 4));
  }

  async function submit(promptText: string) {
    const ok = await send(promptText, files);
    if (ok) {
      setText("");
      setFiles([]);
    }
  }

  const visibleMessages = messages.filter((m) => textOf(m).length > 0);
  const isEmpty = !loading && visibleMessages.length === 0 && cards.length === 0;

  return (
    <div
      className="relative mx-auto flex h-[calc(100dvh-58px)] max-w-[900px] flex-col"
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-10 m-2 grid place-items-center rounded-2xl border-2 border-dashed border-primary bg-primary/5 text-body-md font-semibold text-primary">
          Datei hier ablegen, um sie einzulesen
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 pb-4 pt-8 md:px-2">
        {isEmpty ? (
          <div className="flex flex-col gap-6 pt-10">
            <div>
              <h1 className="font-serif text-[30px] font-semibold italic leading-tight text-on-background">Was steht an?</h1>
              <p className="mt-2 max-w-[540px] text-body-md font-body-md text-on-surface-variant">
                Schreib mir, was du brauchst — eine Speisekarte einlesen, ein Angebot erstellen, eine Frage zu einem Vorgang. Ich frage nach, bevor etwas rausgeht.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                  <Icon name="upload_file" className="text-[18px]" /> Speisekarte hochladen
                </Button>
                <span className="text-body-sm text-on-surface-variant">oder Datei irgendwo hierher ziehen</span>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {SUGGESTIONS.map((s) => (
                <Card key={s.title} type="button" onClick={() => void submit(s.prompt)} disabled={busy}
                  as="button" className="flex flex-col gap-3 p-4 text-left transition-shadow hover:shadow-[0_10px_22px_-12px_rgba(45,38,26,0.28)] disabled:opacity-60">
                  <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-primary/10 text-primary"><Icon name={s.icon} className="text-[18px]" /></span>
                  <span className="text-body-md font-semibold leading-snug text-on-background">{s.title}</span>
                  <span className="mt-auto text-label-md font-semibold text-primary">Los →</span>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {visibleMessages.map((m) => {
              const text = textOf(m);
              const contextLabel = m.role === "user" ? internalContextLabel(text) : null;
              if (contextLabel) {
                return (
                  <div key={m.id} className="mx-auto flex items-center gap-1.5 rounded-full bg-surface-container-low px-3 py-1.5 text-[11px] text-on-surface-variant">
                    {contextLabel}
                  </div>
                );
              }
              return m.role === "user" ? (
                <div key={m.id} className="ml-auto max-w-[80%]">
                  <div className="rounded-[12px_12px_3px_12px] bg-surface-container-high px-3.5 py-2.5 text-body-md text-on-background dark:bg-surface-variant">{text}</div>
                </div>
              ) : (
                <div key={m.id} className="flex flex-col gap-1.5">
                  <span className="text-label-md font-semibold uppercase tracking-wide text-on-surface-variant">MAESTRO</span>
                  <div className="whitespace-pre-wrap text-body-md leading-relaxed text-on-background">{stripMarkdown(text)}</div>
                </div>
              );
            })}

            {cards.map((card, i) => <CardView key={`${card.type}-${i}`} card={card} busy={busy} onAction={runAction} onApprove={approve} onReject={reject} onSend={send} />)}

            {busy && (
              <div className="flex items-center gap-2 text-body-sm font-semibold text-on-background">
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary/20 border-t-primary" /> MAESTRO arbeitet …
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-surface-variant bg-surface-container-lowest px-4 py-3 md:px-2 dark:border-white/10 dark:bg-night-bar">
        {error && <div className="mb-2"><ErrorNote message={error} /></div>}
        {files.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-outline-variant px-2.5 py-1 text-label-md text-on-surface-variant">
                <Icon name={f.type === "application/pdf" ? "picture_as_pdf" : "image"} className="text-[15px]" />
                {f.name}
                <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}><Icon name="close" className="text-[15px]" /></button>
              </span>
            ))}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); void submit(text); }}
          className="flex items-end gap-2 rounded-[13px] border border-outline-variant bg-surface-container-lowest p-2 pl-3 dark:bg-inverse-surface">
          <Button type="button" variant="ghost" className="shrink-0 gap-1 px-2.5" onClick={() => fileInputRef.current?.click()} disabled={busy} aria-label="Datei anhängen">
            <Icon name="attach_file" className="text-[19px]" /><span className="hidden sm:inline">Anhängen</span>
          </Button>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
          <textarea value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void submit(text); } }}
            rows={1} placeholder="Schreib MAESTRO, was ansteht …"
            className="max-h-40 flex-1 resize-none bg-transparent py-2 text-body-md text-on-background outline-none placeholder:text-on-surface-variant/60 dark:placeholder:text-on-surface-variant/80" />
          <button type="submit" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-on-primary disabled:opacity-50"
            disabled={busy || (text.trim() === "" && files.length === 0)} aria-label="Senden">
            <Icon name="arrow_upward" className="text-[18px]" />
          </button>
        </form>
        <p className="mt-2 text-center text-label-md text-on-surface-variant/70">Dateien anhängen oder hierher ziehen · MAESTRO fragt nach, bevor etwas rausgeht</p>
      </div>
    </div>
  );
}
